
import React, { createContext, useContext, useState } from 'react';
import { Notification, NotificationContextType } from '@/types/notification';
import { 
  showNotificationToast, 
  getUnreadCount, 
  DEFAULT_NOTIFICATIONS 
} from '@/utils/notificationUtils';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>(DEFAULT_NOTIFICATIONS);
  const unreadCount = getUnreadCount(notifications);

  const addNotification = (title: string, message: string, isPremiere: boolean = false) => {
    const newNotification: Notification = {
      id: Date.now(),
      title,
      message,
      isRead: false,
      date: new Date(),
      isPremiere
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    showNotificationToast(title, message);
  };

  const markAsRead = (id: number) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, isRead: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
  };

  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        unreadCount, 
        addNotification, 
        markAsRead, 
        markAllAsRead 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
